/**
 * AudioContext lifecycle + master gains + asset loading.
 *
 * Autoplay policy: the context is created/resumed only from a user gesture —
 * the title-screen "TAP TO START" doubles as that gesture (spec 5.1). Song
 * decode failures return null so the caller falls back to SilentClock (5.5).
 */
import { AudioConductor } from "./audioConductor";
import type { SongAsset } from "../data/songs";
import { DEATH_LOWPASS_HZ, OPEN_LOWPASS_HZ } from "../config/tuning";

export type SfxName =
  | "jump"
  | "land"
  | "bit"
  | "death"
  | "checkpoint"
  | "onbeat"
  | "boost"
  | "thunk"
  | "whoosh";

/** Root frequency per song key for pitched SFX (spec 5.3). */
const KEY_ROOT_HZ: Record<string, number> = { Am: 220, Em: 164.81, Bm: 246.94 };

export class AudioSystem {
  private ctx: AudioContext | null = null;
  private musicGain: GainNode | null = null;
  private musicFilter: BiquadFilterNode | null = null;
  private sfxGain: GainNode | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private sfxRootHz = 220;

  /** Create/resume the context. MUST be called from a user-gesture handler. */
  ensureUnlocked(): AudioContext | null {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
        // music → low-pass (death sweep) → destination
        this.musicFilter = this.ctx.createBiquadFilter();
        this.musicFilter.type = "lowpass";
        this.musicFilter.frequency.value = OPEN_LOWPASS_HZ;
        this.musicFilter.connect(this.ctx.destination);
        this.musicGain = this.ctx.createGain();
        this.musicGain.connect(this.musicFilter);
        this.sfxGain = this.ctx.createGain();
        this.sfxGain.connect(this.ctx.destination);
      } catch {
        return null; // no Web Audio — SilentClock world
      }
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    return this.ctx;
  }

  /** Death feedback (3.3): sweep the music dark. */
  deathSweep(): void {
    if (this.ctx && this.musicFilter) {
      this.musicFilter.frequency.setTargetAtTime(DEATH_LOWPASS_HZ, this.ctx.currentTime, 0.05);
    }
  }

  /** Reopen the filter (retry/respawn/new run). */
  openFilter(): void {
    if (this.ctx && this.musicFilter) {
      this.musicFilter.frequency.setTargetAtTime(OPEN_LOWPASS_HZ, this.ctx.currentTime, 0.08);
    }
  }

  /** Set the SFX pitch center from the current song's key. */
  setSongKey(key: string | undefined): void {
    this.sfxRootHz = KEY_ROOT_HZ[key ?? "Am"] ?? 220;
  }

  /** Tiny synthesized one-shots — pitched to the song key, no assets.
   *  `delayS` schedules ahead on the ctx timeline (R2 §2.1: the head-bump
   *  thunk lands on the nearest 16th). */
  sfx(name: SfxName, delayS = 0): void {
    if (!this.ctx || !this.sfxGain) return;
    const t = this.ctx.currentTime + Math.max(0, delayS);
    const root = this.sfxRootHz;
    switch (name) {
      case "jump":
        this.blip("triangle", root * 1.5, root * 3, t, 0.09, 0.25);
        break;
      case "land":
        this.blip("sine", root * 0.75, root * 0.5, t, 0.06, 0.18);
        break;
      case "bit":
        this.blip("triangle", root * 4, root * 4, t, 0.12, 0.3);
        this.blip("triangle", root * 6, root * 6, t + 0.06, 0.12, 0.2);
        break;
      case "death":
        this.blip("sawtooth", root, root * 0.25, t, 0.3, 0.35);
        break;
      case "checkpoint":
        this.blip("triangle", root * 2, root * 2, t, 0.1, 0.25);
        this.blip("triangle", root * 3, root * 3, t + 0.09, 0.14, 0.25);
        break;
      case "onbeat":
        this.blip("sine", root * 8, root * 8, t, 0.08, 0.2);
        break;
      case "boost":
        this.blip("sawtooth", root * 2, root * 5, t, 0.18, 0.22);
        break;
      case "thunk": // dull roof contact — low, short, unmistakable
        this.blip("sine", root * 0.5, root * 0.35, t, 0.09, 0.4);
        break;
      case "whoosh": // near-miss at speed (R2-M4 P1) — a quick airy sweep
        this.blip("sawtooth", root * 6, root * 1.5, t, 0.12, 0.06);
        this.blip("sawtooth", root * 5.6, root * 1.4, t + 0.01, 0.12, 0.05);
        break;
    }
  }

  // --- wind/engine layer (R2-M4 VFX-1): filtered noise whose gain and
  // brightness ride effRate — the throttle is audible even between notes
  private windSource: AudioBufferSourceNode | null = null;
  private windFilter: BiquadFilterNode | null = null;
  private windGain: GainNode | null = null;

  /**
   * Keep the wind/engine loop matched to the current effective rate.
   * Call every frame; creates the loop lazily, fades out when not running.
   */
  setEngineState(effRate: number, running: boolean): void {
    if (!this.ctx || !this.sfxGain) return;
    if (running && !this.windSource) {
      const len = Math.floor(this.ctx.sampleRate * 1.0);
      const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < len; i++) {
        // render-side noise only — never feeds the sim
        // eslint-disable-next-line no-restricted-properties
        data[i] = Math.random() * 2 - 1;
      }
      this.windSource = this.ctx.createBufferSource();
      this.windSource.buffer = buf;
      this.windSource.loop = true;
      this.windFilter = this.ctx.createBiquadFilter();
      this.windFilter.type = "bandpass";
      this.windFilter.Q.value = 0.9;
      this.windGain = this.ctx.createGain();
      this.windGain.gain.value = 0;
      this.windSource.connect(this.windFilter);
      this.windFilter.connect(this.windGain);
      this.windGain.connect(this.sfxGain);
      this.windSource.start();
    }
    if (this.windGain && this.windFilter) {
      const t = this.ctx.currentTime;
      if (running) {
        // 0.6→1.6 maps to a whisper→roar; center pitch climbs with rate
        const span = Math.max(0, Math.min(1, (effRate - 0.6) / 1.0));
        this.windGain.gain.setTargetAtTime(0.02 + 0.12 * span * span, t, 0.1);
        this.windFilter.frequency.setTargetAtTime(220 + 1400 * span, t, 0.1);
      } else {
        this.windGain.gain.setTargetAtTime(0, t, 0.08);
      }
    }
  }

  private iceSource: AudioBufferSourceNode | null = null;
  private iceGain: GainNode | null = null;

  /** Soft icy slide loop while grounded on ice (R2 Feature C). */
  setIceSliding(on: boolean): void {
    if (!this.ctx || !this.sfxGain) return;
    if (on && !this.iceSource) {
      const len = Math.floor(this.ctx.sampleRate * 0.5);
      const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const data = buf.getChannelData(0);
      let lp = 0;
      for (let i = 0; i < len; i++) {
        // render-side noise only — never feeds the sim
        // eslint-disable-next-line no-restricted-properties
        const n = Math.random() * 2 - 1;
        lp += 0.04 * (n - lp);
        data[i] = lp * 2.2;
      }
      this.iceSource = this.ctx.createBufferSource();
      this.iceSource.buffer = buf;
      this.iceSource.loop = true;
      this.iceGain = this.ctx.createGain();
      this.iceGain.gain.value = 0;
      this.iceGain.gain.setTargetAtTime(0.14, this.ctx.currentTime, 0.08);
      this.iceSource.connect(this.iceGain);
      this.iceGain.connect(this.sfxGain);
      this.iceSource.start();
    } else if (!on && this.iceSource) {
      const src = this.iceSource;
      const gain = this.iceGain!;
      gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.06);
      setTimeout(() => {
        try {
          src.stop();
        } catch {
          // already stopped
        }
        src.disconnect();
        gain.disconnect();
      }, 300);
      this.iceSource = null;
      this.iceGain = null;
    }
  }

  private blip(
    type: OscillatorType,
    fromHz: number,
    toHz: number,
    at: number,
    durS: number,
    peak: number,
  ): void {
    if (!this.ctx || !this.sfxGain) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(fromHz, at);
    if (toHz !== fromHz) osc.frequency.exponentialRampToValueAtTime(toHz, at + durS);
    gain.gain.setValueAtTime(peak, at);
    gain.gain.exponentialRampToValueAtTime(0.001, at + durS);
    osc.connect(gain);
    gain.connect(this.sfxGain);
    osc.start(at);
    osc.stop(at + durS + 0.02);
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  get sfxOut(): AudioNode | null {
    return this.sfxGain;
  }

  /** 0..1 */
  setMusicVolume(v: number): void {
    if (this.musicGain && this.ctx) {
      this.musicGain.gain.setTargetAtTime(v * v, this.ctx.currentTime, 0.05);
    }
  }

  setSfxVolume(v: number): void {
    if (this.sfxGain && this.ctx) {
      this.sfxGain.gain.setTargetAtTime(v * v, this.ctx.currentTime, 0.05);
    }
  }

  suspend(): void {
    if (this.ctx && this.ctx.state === "running") void this.ctx.suspend();
  }

  resume(): void {
    if (this.ctx && this.ctx.state === "suspended") void this.ctx.resume();
  }

  /** Prefer opus/ogg, fall back to m4a (Safari). */
  static pickFile(files: { ogg: string; m4a: string }): string {
    const probe = document.createElement("audio");
    return probe.canPlayType('audio/ogg; codecs="opus"') !== "" ? files.ogg : files.m4a;
  }

  /**
   * Fetch + decode a song and wrap it in an AudioConductor.
   * Returns null on any failure — caller falls back to SilentClock.
   */
  async loadSong(song: SongAsset): Promise<AudioConductor | null> {
    const ctx = this.ensureUnlocked();
    if (!ctx || !this.musicGain || !song.files) return null;
    try {
      let buffer = this.buffers.get(song.id);
      if (!buffer) {
        const url = AudioSystem.pickFile(song.files);
        const res = await fetch(url);
        if (!res.ok) return null;
        buffer = await ctx.decodeAudioData(await res.arrayBuffer());
        this.buffers.set(song.id, buffer);
      }
      return new AudioConductor(ctx, buffer, song, this.musicGain);
    } catch {
      return null;
    }
  }

  /** Metronome click for calibration, scheduled on the audio timeline. */
  click(atCtxTime: number, accent: boolean): void {
    if (!this.ctx || !this.sfxGain) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.frequency.value = accent ? 1568 : 1046; // G6 / C6
    gain.gain.setValueAtTime(0.5, atCtxTime);
    gain.gain.exponentialRampToValueAtTime(0.001, atCtxTime + 0.06);
    osc.connect(gain);
    gain.connect(this.sfxGain);
    osc.start(atCtxTime);
    osc.stop(atCtxTime + 0.08);
  }
}
